import { toArray } from '../../utils/array'
import { parseNumber } from '../../utils/number'
import { type FreightClass } from './FreightClass'
import { type PackageType, type WeightType } from './PackageType'

export type CommodityPackageType =
  | 'bags'
  | 'boxes'
  | 'bulk_containers'
  | 'bundles_and_rolls'
  | 'cartons'
  | 'drums'
  | 'gas_cylinders'
  | 'jerricans'
  | 'pails_or_cans'
  | 'pieces'
  | 'tanks'
  | 'totes'
  | 'totes_or_tanks'

export type Commodity = {
  uuid?: string
  description?: string
  packageType?: CommodityPackageType | PackageType
  packageCount?: string | number
  weight?: string | number
  hazmat?: boolean
  freightClass?: FreightClass
  nmfcCode?: string
  hazmatShippingName?: string
  hazmatClass?: HazmatClass
  hazmatPackingGroup?: HazmatPackingGroup
  unNumber?: string
}

export const commodityPackageTypesMap = new Map<CommodityPackageType, string>([
  ['bags', 'Bags'] as const,
  ['boxes', 'Boxes'] as const,
  ['bulk_containers', 'Bulk Containers'] as const,
  ['bundles_and_rolls', 'Bundles and Rolls'] as const,
  ['cartons', 'Cartons'] as const,
  ['drums', 'Drums'] as const,
  ['gas_cylinders', 'Gas Cylinders'] as const,
  ['jerricans', 'Jerricans'] as const,
  ['pails_or_cans', 'Pails or Cans'] as const,
  ['pieces', 'Pieces'] as const,
  ['tanks', 'Tanks'] as const,
  ['totes', 'Totes'] as const,
  ['totes_or_tanks', 'Totes or Tanks'] as const,
])

export type HazmatClass =
  | 'explosive_non_specified'
  | 'mass_explosive'
  | 'projection_explosive'
  | 'minor_fire_explosive'
  | 'minor_explosive'
  | 'insensitive_mass_explosive'
  | 'insensitive_minor_explosive'
  | 'gas_non_specified'
  | 'flammable_gas'
  | 'non_flammable_non_poisonous_or_oxygen_gas'
  | 'poison_gas'
  | 'flammable_liquid'
  | 'solid_non_specified'
  | 'flammable_solid'
  | 'spontaneously_combustible_solid'
  | 'dangerous_when_wet'
  | 'oxidizer_non_specified'
  | 'oxidizer'
  | 'organic_peroxide'
  | 'poison'
  | 'radioactive'
  | 'minor_blast_explosive'
  | 'insensitive_explosive'
  | 'very_insensitive_explosive'
  | 'non_flammable_gas'
  | 'combustible_liquid'
  | 'spontaneously_combustible'
  | 'poisonous_toxic'
  | 'corrosive'
  | 'miscellaneous_dangerous_good'

export const hazmatClassesMap = new Map<HazmatClass, string>([
  ['minor_blast_explosive', '1.4 Minor Blast Explosive'] as const,
  ['insensitive_explosive', '1.5 Insensitive Explosive'] as const,
  ['very_insensitive_explosive', '1.6 Very Insensitive Explosive'] as const,
  ['flammable_gas', '2.1 Flammable Gas'] as const,
  ['non_flammable_gas', '2.2 Non-Flammable Gas'] as const,
  ['poison_gas', '2.3 Poison Gas'] as const,
  ['combustible_liquid', '3 Combustible Liquid'] as const,
  ['flammable_liquid', '3 Flammable Liquid'] as const,
  ['flammable_solid', '4.1 Flammable Solid'] as const,
  ['spontaneously_combustible', '4.2 Spontaneously Combustible'] as const,
  ['dangerous_when_wet', '4.3 Dangerous When Wet'] as const,
  ['oxidizer', '5.1 Oxidizer'] as const,
  ['organic_peroxide', '5.2 Organic Peroxide'] as const,
  ['poisonous_toxic', '6 Poisonous/Toxic'] as const,
  ['corrosive', '8 Corrosive'] as const,
  ['miscellaneous_dangerous_good', '9 Miscellaneous Dangerous Good'] as const,
])

export type HazmatPackingGroup = 'group_1' | 'group_2' | 'group_3'

export const hazmatPackingGroupMap = new Map<HazmatPackingGroup, string>([
  ['group_1', 'Group I'] as const,
  ['group_2', 'Group II'] as const,
  ['group_3', 'Group III'] as const,
])

type TotalWeightItemProps = {
  weightType?: WeightType
  packageCount?: string | number
  commodities?: Array<Pick<Commodity, 'weight'>>
}

export function getFreightClass(commodities?: Commodity[]) {
  if (!commodities) {
    return
  }
  const maxFreightClassCommodity = commodities.reduce<FreightClass | null>(
    (highestFreightClass, commodity) => {
      if (!commodity.freightClass) {
        return highestFreightClass
      }

      const currentFreightClass = Number(commodity.freightClass)

      if (!highestFreightClass || currentFreightClass > Number(highestFreightClass)) {
        return commodity.freightClass
      }

      return highestFreightClass
    },
    null
  )

  if (maxFreightClassCommodity === null) {
    return
  }

  return maxFreightClassCommodity
}

export function getPackageDescription(commodities?: Commodity[]) {
  if (!commodities) {
    return
  }
  return commodities.find((commodity: Commodity) => commodity.description)?.description
}

export function pieceCountFromCommodity(commodity: Pick<Commodity, 'packageCount'>): number {
  return parseNumber(commodity.packageCount) || 0
}

export function calculateTotalWeight(
  items?: TotalWeightItemProps | TotalWeightItemProps[]
): number {
  return toArray(items).reduce((totalWeight, item) => {
    if (!item.commodities) {
      return totalWeight
    }

    const { weightType, commodities } = item

    const partialWeight = commodities.reduce((commoditiesTotalWeight, commodity) => {
      const parsedWeight = Number.parseFloat(String(commodity.weight)) || 0

      if (weightType === 'per_package') {
        const parsedPackageCount = Number.parseInt(String(item.packageCount), 10) || 0

        return commoditiesTotalWeight + parsedWeight * parsedPackageCount
      }

      return commoditiesTotalWeight + parsedWeight
    }, 0)

    return totalWeight + partialWeight
  }, 0)
}
